import { ImageTransformation } from "./sprites/imageTransformation";

let ASSETS_DIR = "/assets-hd/";

export function setAssetsDir(dir: string): void {
	ASSETS_DIR = dir;
}
// Resolved at build time by Vite — picks up every image file in the folder automatically.
const allImageFiles = Object.keys(import.meta.glob("/public/assets-hd/*.png", { eager: false })).map((path) =>
	path.replace("/public/assets-hd/", ""),
);

const imageCache = new Map<string, HTMLCanvasElement>();
const rawImageCache = new Map<string, Promise<HTMLImageElement>>();

function buildCacheKey(filename: string, width: number, height: number, transformation: ImageTransformation): string {
	return `${filename}|${width}|${height}|${transformation.toCacheKey()}`;
}

function loadRawImage(filename: string): Promise<HTMLImageElement> {
	const cached = rawImageCache.get(filename);
	if (cached) return cached;

	const promise = new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error(`Failed to load image: ${filename}`));
		img.src = ASSETS_DIR + filename;
	});

	rawImageCache.set(filename, promise);
	return promise;
}

function applyTransformations(sourceCanvas: HTMLCanvasElement, transformation: ImageTransformation): HTMLCanvasElement {
	let canvas = sourceCanvas;

	const rotate = transformation.rotate % 4;
	for (let i = 0; i < rotate; i++) {
		canvas = rotateImage90(canvas);
	}
	if (transformation.flipH) {
		canvas = flipImageHorizontally(canvas);
	}
	if (transformation.flipV) {
		canvas = flipImageVertically(canvas);
	}

	return canvas;
}

function rotateImage90(source: HTMLCanvasElement): HTMLCanvasElement {
	const w = source.width;
	const h = source.height;
	const canvas = document.createElement("canvas");
	canvas.width = h;
	canvas.height = w;
	const ctx = canvas.getContext("2d")!;
	ctx.translate(h / 2, w / 2);
	ctx.rotate(Math.PI / 2);
	ctx.drawImage(source, -w / 2, -h / 2);
	return canvas;
}

function flipImageHorizontally(source: HTMLCanvasElement): HTMLCanvasElement {
	const canvas = document.createElement("canvas");
	canvas.width = source.width;
	canvas.height = source.height;
	const ctx = canvas.getContext("2d")!;
	ctx.scale(-1, 1);
	ctx.translate(-source.width, 0);
	ctx.drawImage(source, 0, 0);
	return canvas;
}

function flipImageVertically(source: HTMLCanvasElement): HTMLCanvasElement {
	const canvas = document.createElement("canvas");
	canvas.width = source.width;
	canvas.height = source.height;
	const ctx = canvas.getContext("2d")!;
	ctx.scale(1, -1);
	ctx.translate(0, -source.height);
	ctx.drawImage(source, 0, 0);
	return canvas;
}

export async function getImage(
	filename: string,
	width: number,
	height: number,
	transformation?: ImageTransformation,
): Promise<HTMLCanvasElement> {
	const transform = transformation ?? new ImageTransformation();
	const key = buildCacheKey(filename, width, height, transform);

	const cached = imageCache.get(key);
	if (cached) return cached;

	const rawImage = await loadRawImage(filename);

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d")!;
	ctx.drawImage(rawImage, 0, 0, width, height);

	const result = applyTransformations(canvas, transform);
	imageCache.set(key, result);
	return result;
}

export async function preloadAll(): Promise<void> {
	await Promise.all(allImageFiles.map((f) => loadRawImage(f)));
}

export function clearCache(): void {
	imageCache.clear();
	rawImageCache.clear();
}
